import { Component, EventEmitter, inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as ActionCable from '@rails/actioncable';
import { Cable } from '@services/cable';
import { RetakeDocumentPayload } from 'src/app/interfaces/retake-document-payload.interface';
import { SessionDetailsComponent } from '../session-details/session-details.component';
import { CommonModule } from '@angular/common';
import { WalkStepPhotoComponent } from '../walk-step-photo/walk-step-photo.component';
import { WalkStepIDComponent } from '../walk-step-id/walk-step-id.component';
import { WalkStepIntroComponent } from '../walk-step-intro/walk-step-intro.component';
import { WalkStepBiometricsComponent } from '../walk-step-biometrics/walk-step-biometrics.component';
import { AgentVerifyBiometricsComponent } from '../agent-verify-biometrics/agent-verify-biometrics.component';
import { WalkStepCompleteComponent } from '../walk-step-complete/walk-step-complete.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AgentVerifyIdComponent } from '../agent-verify-id/agent-verify-id.component';
import { AgentVerifyAttendanceComponent } from '../agent-verify-attendance/agent-verify-attendance.component';
import { switchMap, tap, Subject, startWith } from 'rxjs';
import { SessionsService } from '@services/sessions.service';
import { Identification, Session, SessionPermissions } from 'src/app/queries/generated';
import { Tokbox } from '@models/tokbox';

interface SessionChannelData {
  started?: boolean;
  finished?: boolean;
  currentStep?: number;
  biometricsUpdate?: any;
  biometrics?: any;
  nextStep?: number;
}

@Component({
  selector: 'lv-session',
  templateUrl: './session.component.html',
  styleUrls: ['session.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    SessionDetailsComponent,
    WalkStepPhotoComponent,
    WalkStepIDComponent,
    WalkStepIntroComponent,
    WalkStepBiometricsComponent,
    AgentVerifyBiometricsComponent,
    WalkStepCompleteComponent,
    TranslateModule,
    AgentVerifyIdComponent,
    AgentVerifyAttendanceComponent
  ]
})
export class SessionComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cable = inject(Cable);
  private translate = inject(TranslateService);
  private sessionsService = inject(SessionsService);

  passed = new EventEmitter<boolean>();
  failed = new EventEmitter<string>();
  rejected = new EventEmitter<RetakeDocumentPayload>();

  step = 5; // live agent step
  nextStep: number;
  biometricsPassed = false;

  /// Things set after load the session.
  identification: Identification;
  can: SessionPermissions;
  liveAgent: boolean;
  tokbox: Tokbox;
  channel: ActionCable.Channel;

  /// Subject to trigger session reloads
  private sessionReloadTrigger$ = new Subject<void>();

  session$ = this.sessionReloadTrigger$.pipe(
    startWith(null),
    switchMap(() => this.route.params),
    switchMap((params) => this.sessionsService.find(params.id, true)),
    tap((session: Session) => {
      this.identification = session.activeIdentification;
      this.can = session.can;
      this.liveAgent = session.liveAgent;

      this.afterLoadSession(session);
    })
  );

  get continueKeyTranslated(): string {
    return this.translate.instant('session.component.continue.' + this.continueKey());
  }

  ngOnDestroy() {
    if (this.channel) {
      this.channel.unsubscribe();
    }
  }

  next() {
    if (this.nextStep) {
      this.channel.perform('rebroadcast', { currentStep: this.nextStep });
      this.nextStep = undefined;
      return;
    }

    const shouldPass = this.step === 7;
    this.channel.perform('rebroadcast', { next: true });
    if (shouldPass) {
      setInterval(() => {
        if (this.canPass()) {
          this.passed.emit(true);
        }
      }, 1000);
    }
  }

  nextEnabled(): boolean {
    // return false;
    switch (this.step) {
      case 3: // face photo
      case 4: // id photo
        return true; // worry about these guys later
      case 5: // initial live agent screen
        return this.tokbox && this.tokbox.connected;
      case 6: // biometrics
        return this.biometricsPassed;
      case 7: // ready
        return true;
      default:
        return false;
    }
  }

  private canPass(): boolean {
    return this.can.pass || (this.liveAgent && this.step === 7);
  }

  /**
   * Validates the session existance and ownership,
   * so we can set the tokbox and connect to the channel.
   */
  private afterLoadSession(session: Session) {
    if (!session) {
      this.router.navigateByUrl('/');
      return;
    }

    if (!this.can.verify) {
      this.router.navigateByUrl('/');
      return;
    }

    if (session.tokbox) {
      this.tokbox = new Tokbox(session.tokbox);
    }

    this.connectToChannel(session);
  }

  private connectToChannel(session: Session) {
    const cableParams = {
      channel: 'SessionChannel',
      token: session.token
    };

    this.channel = this.cable.create(cableParams, (data: SessionChannelData) => {
      if (data.started || data.finished) {
        this.sessionReloadTrigger$.next();
      }
      if (data.currentStep) {
        this.step = data.currentStep;
      }
      if (data.nextStep) {
        this.nextStep = data.nextStep;
      }
      if (data.biometrics) {
        this.biometricsPassed = data.biometrics.verified === true || data.biometrics.trained === true;
      }
    });
  }

  private continueKey(): string {
    switch (this.step) {
      case 3: // face photo
        return this.nextStep ? 'return-biometrics' : 'id-photo';
      case 4: // id photo
        return this.nextStep ? 'return-biometrics' : 'biometrics';
      case 5: // initial live agent screen
        return 'biometrics';
      case 6: // biometrics
        return 'ready';
      case 7: // ready
        return 'begin';
      default:
        return 'begin';
    }
  }
}
